import type { SimResult } from '../engine/types'
import { useRollingNumber } from '../hooks/useRollingNumber'
import { formatINRCompact, formatPercent } from '../utils/formatINR'

/**
 * The number this whole product exists to produce.
 *
 * Everything here serves one job: make the probability legible in the half-second
 * before someone decides whether to keep playing with the sliders. It is the
 * thing that gets screenshotted, so it carries the display face, the largest
 * type on the page, and the only colour on screen that moves.
 */

export interface HeadlineComparison {
  /** Label of the scenario the hero number belongs to. */
  activeLabel: string
  /** The age the ACTIVE (hero) probability is quoted against. */
  activeTargetAge: number
  otherLabel: string
  /** CSS custom property name for the other scenario's hue. */
  otherHue: string
  /** The age the OTHER scenario's probability is quoted against. */
  otherTargetAge: number
  other: SimResult | null
}

interface HeadlineProps {
  result: SimResult | null
  targetAge: number
  /**
   * The requirement BEFORE withdrawal tax — `result.requiredAtTarget` divided by
   * the gross-up, which is exactly the pre-tax figure since the gross-up is the
   * only thing separating them. `plan.ts` exposes `withdrawalGrossUpFor`.
   *
   * Required, not optional, deliberately. If this were optional a caller that
   * forgot to pass it would silently render the old single-figure line and every
   * check would still pass — the feature would be absent and nothing would say
   * so. As a required prop that omission is a type error.
   */
  naiveRequiredAtTarget: number
  /** Present only while two scenarios are on screen. */
  comparison?: HeadlineComparison | null
}

export function Headline({
  result,
  targetAge,
  naiveRequiredAtTarget,
  comparison = null,
}: HeadlineProps) {
  const probability = result?.probabilityAtTarget ?? 0
  const rolling = useRollingNumber(probability)

  // Cool slate at 0, warming to gold at 100, mixed in OKLCH so it travels
  // through teal instead of through red. Explicitly not a traffic light: a 40%
  // result means "not yet", not "danger", and colouring it like a brake warning
  // would be both wrong and unkind about someone's actual retirement.
  const confidence = `color-mix(in oklch, var(--c-confidence-low), var(--c-confidence-high) ${Math.round(
    rolling * 100,
  )}%)`

  return (
    <header className="text-center">
      <p className="text-[length:var(--text-micro)] uppercase tracking-[var(--tracking-label)] text-[color:var(--c-text-tertiary)]">
        {/* Naming the scenario matters once there are two: the hero number is one
            of them, and an unlabelled 93% beside a chart with two fans is
            ambiguous about which fan produced it. */}
        {comparison !== null && `${comparison.activeLabel} · `}
        chance of FIRE by {Math.round(targetAge)}
      </p>

      <p
        className="numeral-hero text-[length:var(--text-hero)]"
        style={{ color: confidence }}
        // The rolling value is decorative motion; screen readers get the settled
        // number, announced only once it stops moving.
        aria-live="polite"
        aria-atomic="true"
      >
        <span aria-hidden="true">{formatPercent(rolling)}</span>
        <span className="sr-only">
          {formatPercent(probability)} chance of reaching financial independence by age{' '}
          {Math.round(targetAge)}
        </span>
      </p>

      {result !== null && (
        <SupportingLine result={result} naiveRequiredAtTarget={naiveRequiredAtTarget} />
      )}

      {comparison !== null && comparison.other !== null && result !== null && (
        <DeltaLine active={result} comparison={comparison} />
      )}

      {/*
        Sits with the claim, deliberately. The CCPA Guidelines, 2022 measure a
        disclaimer by whether a normally-sighted person reading the page once
        would see it — a qualifier a full screen below the number it qualifies
        does not meet that, however complete its wording.

        Set in the DISPLAY face, matching the number above it. Guideline 11(2) of
        the same rules requires that "the font used in a disclaimer shall be the
        same as that used in the claim", and the claim here is `.numeral-hero`,
        i.e. Space Grotesk. Whether "font" means typeface or also size is
        arguable; matching the typeface satisfies the conservative reading and
        costs nothing. The share card does the same thing for the same reason —
        see `DISCLAIMER` in `src/card/copy.ts`.
      */}
      <p className="mt-1.5 font-[family-name:var(--font-display)] text-[length:var(--text-micro)] text-[color:var(--c-text-muted)]">
        An illustration from assumptions you can change — not a forecast.
      </p>
    </header>
  )
}

/**
 * The comparison, stated in words.
 *
 * This is the line the feature exists for. Two fans on a chart show *that* the
 * scenarios differ; only a sentence says by how much, and it is the part that
 * survives being read aloud, screenshotted, or heard by a screen reader.
 *
 * **Years are whole numbers, deliberately.** `medianFireAge` is an age off the
 * `ages` array, so the difference between two of them is an integer count of
 * years. The brief's illustrative "3.2 years earlier" would need the 50%
 * crossing interpolated out of `successByAge`, which is a different and more
 * arguable statistic; quoting a decimal this cannot support would be false
 * precision on the one number people repeat.
 */
function DeltaLine({
  active,
  comparison,
}: {
  active: SimResult
  comparison: HeadlineComparison
}) {
  const other = comparison.other as SimResult
  /*
   * Subtract the integers the UI PRINTS, not the raw fractions.
   *
   * `formatPercent` rounds each probability independently, so differencing the
   * fractions and rounding that produced sentences the reader could disprove by
   * subtracting what was on screen: 0.3977 vs 0.2640 rendered "40%" and "26%"
   * beside "13 points lower", and 0.744 vs 0.746 rendered "75%" beside "the same
   * odds" under a hero reading 74%. Roughly half of all value pairs were wrong by
   * one. Deriving the delta from the displayed integers cannot disagree with them.
   */
  const points =
    Math.round(other.probabilityAtTarget * 100) - Math.round(active.probabilityAtTarget * 100)

  const timing = fireAgeDelta(active.medianFireAge, other.medianFireAge)

  /*
   * Two probabilities measured at different deadlines are not comparable as a
   * bare points gap, and the eyebrow names only the active scenario's age. Two
   * financially identical plans differing only in target age read as "23 points
   * higher" — a gap that is entirely the deadline moving. Naming the age is the
   * minimum; it is the reader's cue that the comparison is not like-for-like.
   */
  const differentDeadline =
    Math.round(comparison.otherTargetAge) !== Math.round(comparison.activeTargetAge)

  return (
    /*
      Display face, like the hero above it. The disclaimer below is set in the
      display face specifically to satisfy CCPA Guideline 11(2) — "the font used
      in a disclaimer shall be the same as that used in the claim". This line is
      a second claim under that same disclaimer, and shipping it in the body face
      left one disclaimer qualifying two claims in two different fonts.
    */
    <p className="mt-1.5 font-[family-name:var(--font-display)] text-[length:var(--text-label)] text-[color:var(--c-text-secondary)]">
      <span
        className="mr-1 inline-block size-2 rounded-[var(--radius-full)] align-middle"
        style={{ background: `var(${comparison.otherHue})` }}
        aria-hidden="true"
      />
      <span className="numeral">{comparison.otherLabel}</span>
      {': '}
      <span className="numeral">{formatPercent(other.probabilityAtTarget)}</span>
      {differentDeadline && (
        <>
          {' by '}
          <span className="numeral">{Math.round(comparison.otherTargetAge)}</span>
        </>
      )}
      {' — '}
      {points === 0 ? (
        'the same odds'
      ) : (
        <>
          <span className="numeral">{Math.abs(points)}</span>
          {` point${Math.abs(points) === 1 ? '' : 's'} ${points > 0 ? 'higher' : 'lower'}`}
        </>
      )}
      {timing !== null && `, and ${timing}`}
      {'.'}
    </p>
  )
}

/** Null when neither ordering is meaningful — one of the two never gets there. */
function fireAgeDelta(activeAge: number | null, otherAge: number | null): string | null {
  if (otherAge === null) return 'the median path does not get there within this projection'
  if (activeAge === null) return `the median path gets there at ${Math.round(otherAge)}`

  const years = Math.round(activeAge - otherAge)
  if (years === 0) return 'the median path gets there the same year'
  return `the median path gets there ${Math.abs(years)} year${Math.abs(years) === 1 ? '' : 's'} ${
    years > 0 ? 'sooner' : 'later'
  }`
}

function SupportingLine({
  result,
  naiveRequiredAtTarget,
}: {
  result: SimResult
  naiveRequiredAtTarget: number
}) {
  const { medianCorpusAtTarget, requiredAtTarget, medianFireAge } = result
  const shortfall = medianCorpusAtTarget < requiredAtTarget

  /*
    The two figures the feature exists to put side by side: what the corpus has
    to be once withdrawal tax is priced in, and what the old pre-tax rule said.

    The `needed` figure is already tax-adjusted — it comes straight from the
    engine, which was handed a grossed-up multiple — so nothing is recomputed
    here. Only the comparison is new.

    `formatINRCompact` holds three significant figures, so on a short horizon or
    a modest plan the two figures round to the SAME string: the gross-up can be
    0.2%, and "₹1.47 Cr needed (₹1.47 Cr before tax)" states one number twice and
    reads like a bug. In that case quote the drag as a percentage instead, which
    is the honest thing to say when the difference is real but smaller than the
    display can resolve.
  */
  const taxAdjusted = formatINRCompact(requiredAtTarget)
  const beforeTax = formatINRCompact(naiveRequiredAtTarget)
  const drag = naiveRequiredAtTarget > 0 ? requiredAtTarget / naiveRequiredAtTarget - 1 : 0
  /*
    Three cases, not two. The third was found in review.

    A one-year horizon is reachable — `clampPlan` only forces `fireByAge` to be at
    least `currentAge + 1` — and at one year there is no accumulated gain to tax, so
    the gross-up is exactly 1 and the drag is exactly 0. The percentage branch then
    rendered "tax adds 0.0%", which is the same says-nothing failure it was written
    to prevent. With no difference to report, the clause is dropped entirely.
  */
  const taxNote =
    drag <= 0
      ? null
      : beforeTax === taxAdjusted
        ? `tax adds ${(drag * 100).toFixed(drag * 100 < 1 ? 1 : 0)}%`
        : `${beforeTax} before tax`

  return (
    <div className="mt-1 space-y-0.5">
      <p className="text-[length:var(--text-label)] text-[color:var(--c-text-secondary)]">
        <span className="numeral">{formatINRCompact(medianCorpusAtTarget)}</span> projected
        {' · '}
        <span className="numeral text-[color:var(--c-fire)]">{taxAdjusted}</span> needed
        {taxNote !== null && (
          <>
            {' · '}
            <span className="numeral text-[color:var(--c-text-tertiary)]">{taxNote}</span>
          </>
        )}
      </p>

      {/*
        The single most useful sentence on the screen when the target is missed.
        "62%" tells someone they might not make it; "the median path gets there at
        49" tells them what to actually do about it. Only shown when it adds
        something the line above has not already said.
      */}
      <p className="text-[length:var(--text-micro)] text-[color:var(--c-text-tertiary)]">
        {medianFireAge === null
          ? 'The median path does not get there within this projection.'
          : shortfall
            ? `The median path gets there at ${Math.round(medianFireAge)}.`
            : `On track — the median path clears it at ${Math.round(medianFireAge)}.`}
      </p>
    </div>
  )
}
